import { API_HOST, MODERATION_PATH } from './config'
import type { ApiEnvelope } from './types'
import { fetchWithTimeout } from './support'

/**
 * Teacher moderation calls.
 *
 * These go to our API rather than to LiveKit: muting someone else needs room
 * admin authority, which lives on the server with our API secret. The server
 * re-checks from the join code that the caller really is this class's teacher —
 * see Meetings::Moderation.
 *
 * Every action here is one-directional: OFF. There is deliberately no server
 * call that switches a student's microphone or camera back on. Bringing someone
 * back is a REQUEST the student accepts (see useClassroomSignals), because an
 * adult silently opening a child's camera is not something this app should be
 * able to do.
 */

export type ModerationAction = 'mute_audio' | 'mute_video' | 'stop_share'

/**
 * "remove" disconnects a student and revokes their join codes for this class, so
 * their link stops working; an admin mints a new one to readmit them.
 * "mute_all" mutes every student microphone and ignores the target.
 */
export type ClassAction = ModerationAction | 'remove' | 'mute_all'

/** `status` is undefined when no HTTP answer arrived (offline or timed out). */
export class ModerationError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
  }
}

const REQUEST_TIMEOUT_MS = 10_000

export async function moderate(
  credential: { code?: string | null; reconnectToken?: string | null },
  targetIdentity: string | null,
  action: ClassAction,
): Promise<{ muted?: number } | undefined> {
  let response: Response
  try {
    response = await fetchWithTimeout(`${API_HOST}${MODERATION_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      cache: 'no-store',
      body: JSON.stringify({
        code: credential.code ?? undefined,
        reconnect_token: credential.reconnectToken ?? undefined,
        target_identity: targetIdentity ?? undefined,
        // Not `action`: Rails reserves that parameter name for the controller
        // action, and it would be silently overwritten in params.
        moderation_action: action,
      }),
    }, REQUEST_TIMEOUT_MS)
  } catch (error) {
    throw new ModerationError((error as Error)?.message || 'That did not work — try again')
  }

  let envelope: ApiEnvelope<{ muted?: number }> | undefined
  try {
    envelope = (await response.json()) as ApiEnvelope<{ muted?: number }>
  } catch {
    /* classified by status below */
  }

  // Surfaced, unlike diagnostics: a teacher who taps "mute" and gets silence
  // cannot tell whether the class still hears the student.
  if (!response.ok || envelope?.status !== 'success') {
    throw new ModerationError(envelope?.message ?? 'That did not work — try again', response.status)
  }
  return envelope.data
}
